import {
  Directive,
  Input,
  ElementRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  Renderer2,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

/**
 * Translate Directive
 * 
 * Directive for translating element text content
 * 
 * @example
 * ```html
 * <h1 translate="welcome.title"></h1>
 * <p [translate]="'welcome.message'" [translateParams]="{ name: 'John' }"></p>
 * ```
 */
@Directive({
  selector: '[translate]',
  standalone: true,
})
export class TranslateDirective implements OnInit, OnDestroy, OnChanges {
  private readonly transloco = inject(TranslocoService);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private subscription?: Subscription;

  @Input('translate') key?: string;
  @Input() translateParams?: Record<string, any>;
  @Input() translateDefault?: string;

  ngOnInit(): void {
    if (this.key) {
      this.updateTranslation();
      this.subscribeToLocaleChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['key'] || changes['translateParams']) {
      this.updateTranslation();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private updateTranslation(): void {
    if (!this.key) {
      return;
    }

    const translation = this.transloco.translate(this.key, this.translateParams || {});
    const text = this.renderer.createText(translation);
    
    // Clear existing text nodes
    const element = this.el.nativeElement;
    while (element.firstChild) {
      this.renderer.removeChild(element, element.firstChild);
    }
    
    // Add translated text
    this.renderer.appendChild(element, text);
  }

  private subscribeToLocaleChanges(): void {
    this.subscription = this.transloco.langChanges$.subscribe(() => {
      this.updateTranslation();
    });
  }
}

